import { delay, race, select, take, takeEvery } from 'redux-saga/effects';
import { BasketActionTypes } from '../../../enums/ActionEnums';
import { getFredhopperViewSourceId } from '../../../util/fredhopper';
import { emitter, EmitterEvent } from '../../../util/tracking';
import { buildCartUpdatePayload } from '../../../util/TrackingUtil';
import { makeGetBasketId, makeGetBasketMappedProductsState, makeGetBasketPricingState, makeGetBasketTotalQuantityState, } from '../selectors/BasketSelector';
const REFRESH_WAIT_TIMEOUT_MS = 5000;
const fromPdp = (product) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    if (!(product === null || product === void 0 ? void 0 : product.productId))
        return undefined;
    const selectedColor = (_a = product.productColorVariations) === null || _a === void 0 ? void 0 : _a.find(c => c.colorId === product.selectedColorId);
    const selectedSize = (_b = selectedColor === null || selectedColor === void 0 ? void 0 : selectedColor.sizes) === null || _b === void 0 ? void 0 : _b.find((s) => s.sku === product.selectedSku);
    return {
        productId: product.productId,
        brand: (_d = (_c = product.brand) === null || _c === void 0 ? void 0 : _c.description) !== null && _d !== void 0 ? _d : '',
        title: (_f = (_e = product.productTranslatedProperties) === null || _e === void 0 ? void 0 : _e.title) !== null && _f !== void 0 ? _f : '',
        colour: (_g = selectedColor === null || selectedColor === void 0 ? void 0 : selectedColor.color) !== null && _g !== void 0 ? _g : '',
        colourCode: (_h = selectedColor === null || selectedColor === void 0 ? void 0 : selectedColor.colorCode) !== null && _h !== void 0 ? _h : '',
        size: (_j = selectedSize === null || selectedSize === void 0 ? void 0 : selectedSize.fullCommercialSize) !== null && _j !== void 0 ? _j : '',
        price: (_l = (_k = selectedColor === null || selectedColor === void 0 ? void 0 : selectedColor.prices) === null || _k === void 0 ? void 0 : _k.SELL) === null || _l === void 0 ? void 0 : _l.original,
        buyingGroup: (_m = product.buyingGroup) !== null && _m !== void 0 ? _m : '',
        gender: (_o = product.gender) !== null && _o !== void 0 ? _o : '',
    };
};
const fromTile = (tile) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    return ({
        productId: (_a = tile.productId) !== null && _a !== void 0 ? _a : '',
        brand: (_b = tile.brand) !== null && _b !== void 0 ? _b : '',
        title: (_c = tile.description) !== null && _c !== void 0 ? _c : '',
        colour: (_d = tile.color) !== null && _d !== void 0 ? _d : '',
        colourCode: (_e = tile.colorCode) !== null && _e !== void 0 ? _e : '',
        size: (_f = tile.sizeDescription) !== null && _f !== void 0 ? _f : '',
        price: tile.price,
        buyingGroup: (_g = tile.buyingGroup) !== null && _g !== void 0 ? _g : '',
        gender: (_h = tile.gender) !== null && _h !== void 0 ? _h : '',
    });
};
const findTileBySku = (tiles, sku) => (sku == null ? undefined : tiles.find(p => String(p.sku) === String(sku)));
const getPdpProduct = (state) => state.product;
const getMappedProducts = makeGetBasketMappedProductsState();
const getTotalQuantity = makeGetBasketTotalQuantityState();
const getPricing = makeGetBasketPricingState();
const getBasketId = makeGetBasketId();
export function* handleCartUpdate(action) {
    var _a;
    try {
        const { product } = action.payload;
        const payloadTile = action.type === BasketActionTypes.REMOVE_PRODUCT_SUCCESS ? action.payload.tile : undefined;
        const pdpProduct = yield select(getPdpProduct);
        let details = payloadTile ? fromTile(payloadTile) : fromPdp(pdpProduct);
        // Wait for fresh basket state. Skip emission if no refresh arrives (avoids stale totals).
        const { refreshed } = yield race({
            refreshed: take(BasketActionTypes.REFRESH_COMPLETE),
            timeout: delay(REFRESH_WAIT_TIMEOUT_MS),
        });
        if (!refreshed)
            return;
        const mappedProducts = yield select(getMappedProducts);
        const totalQuantity = yield select(getTotalQuantity);
        const pricing = yield select(getPricing);
        const basketId = yield select(getBasketId);
        if (!details) {
            const basketTile = findTileBySku(mappedProducts, product.sku);
            if (basketTile)
                details = fromTile(basketTile);
        }
        const emitAction = action.type === BasketActionTypes.REMOVE_PRODUCT_SUCCESS ? 'remove' : 'add';
        const emitQuantity = action.type === BasketActionTypes.UPDATE_PRODUCT_SUCCESS ? action.payload.quantity : product.quantity;
        // A -1 quantity decrease dispatches the same REMOVE_PRODUCT_SUCCESS action as a full
        // removal (see DecreaseProductQuantitySaga). Post-refresh basket state is the source of
        // truth: if the sku is still present, it was a decrease, not a removal.
        const isFullRemoval = action.type === BasketActionTypes.REMOVE_PRODUCT_SUCCESS ? !findTileBySku(mappedProducts, product.sku) : true;
        const isAdd = action.type === BasketActionTypes.ADD_SUCCESS;
        emitter.emit(EmitterEvent.CART_UPDATE, Object.assign(Object.assign({}, buildCartUpdatePayload(emitAction, Object.assign(Object.assign({}, (details !== null && details !== void 0 ? details : {})), { productId: (_a = details === null || details === void 0 ? void 0 : details.productId) !== null && _a !== void 0 ? _a : product.productId, sku: product.sku, quantity: emitQuantity, isFullRemoval }), mappedProducts, totalQuantity, pricing, isAdd ? getFredhopperViewSourceId() : undefined)), { basket_token: basketId !== null && basketId !== void 0 ? basketId : '' }));
    }
    catch (_b) {
        // Tracking must never break the basket flow.
    }
}
export function* cartUpdateTrackingSaga() {
    // takeEvery so every cart change emits its own event. `handleCartUpdate` instances run
    // concurrently and share the next REFRESH_COMPLETE; per-action details stay accurate even
    // if multiple events report the same post-refresh totals under rapid interaction.
    yield takeEvery([BasketActionTypes.ADD_SUCCESS, BasketActionTypes.REMOVE_PRODUCT_SUCCESS, BasketActionTypes.UPDATE_PRODUCT_SUCCESS], handleCartUpdate);
}
